/**
 * Moderator-only: email an audience intro link to people who are not in
 * REDI yet.
 *
 * The client's Sept 22 2026 ask, second half. The links and the pages let
 * somebody arrive; this is how they hear about it. Kept behind the moderator
 * flag because, unlike every other send in REDI, the recipients are not on a
 * plan, a claim or an opt-in list. Somebody typed their address in.
 *
 * Every cap here is repeated server side (sendAudienceInvite.ts). These are
 * for the person filling the form, not for safety.
 */
import { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { AdaptiveSheet } from '../common/AdaptiveSheet';
import { Button } from '../common/Button';
import { sendAudienceInvite } from '../../api/audienceInvites';
import { getErrorMessage } from '../../utils/errorHandler';
import { parseEmailList } from '../../utils/emailList';
import { AUDIENCE_IDS, AUDIENCES } from '../../constants/audiences';
import { ROLE_ICONS } from '../../constants/roleIcons';
import { USER_ROLE_META, type UserRole } from '../../types';

const MAX_RECIPIENTS = 25;
const MAX_NOTE = 300;

interface AudienceInviteSheetProps {
  visible: boolean;
  onClose: () => void;
}

export function AudienceInviteSheet({ visible, onClose }: AudienceInviteSheetProps) {
  const { colors } = useTheme();
  const [audience, setAudience] = useState<UserRole>('fire-scout');
  const [recipients, setRecipients] = useState('');
  const [note, setNote] = useState('');
  const [areaName, setAreaName] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<{ text: string; isError: boolean } | null>(null);

  const emails = parseEmailList(recipients);
  const tooMany = emails.length > MAX_RECIPIENTS;
  const canSend = emails.length > 0 && !tooMany && !sending;

  const handleSend = async () => {
    setSending(true);
    setNotice(null);
    try {
      const result = await sendAudienceInvite({
        audience,
        emails,
        note: note.trim() || undefined,
        areaName: areaName.trim() || undefined,
      });
      const parts = [`Sent ${result.sent}`];
      if (result.suppressed) parts.push(`${result.suppressed} unsubscribed`);
      if (result.failed) parts.push(`${result.failed} failed`);
      setNotice({ text: `${parts.join(', ')}.`, isError: result.failed > 0 });
      if (result.failed === 0) setRecipients('');
    } catch (err) {
      // The callable's messages are written to be shown as they are.
      setNotice({ text: getErrorMessage(err), isError: true });
    } finally {
      setSending(false);
    }
  };

  return (
    <AdaptiveSheet isOpen={visible} onRequestClose={onClose} onDismiss={onClose} dialogWidth={460}>
      <AdaptiveSheet.ScrollView
        testID="audience-invite-sheet"
        contentContainerStyle={styles.content}>
        <Text style={[styles.title, { color: colors.text }]}>Invite people to REDI</Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          Each audience gets a different email and a link to a page written for them.
        </Text>

        <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Audience</Text>
        <View style={styles.audienceList}>
          {AUDIENCE_IDS.map((id) => {
            const selected = audience === id;
            const Icon = ROLE_ICONS[id];
            return (
              <Pressable
                key={id}
                onPress={() => setAudience(id)}
                testID={`audience-invite-pick-${id}`}
                accessibilityRole="radio"
                aria-checked={selected}
                accessibilityLabel={USER_ROLE_META[id].label}
                style={[
                  styles.audienceRow,
                  CURSOR_POINTER,
                  {
                    borderColor: selected ? colors.action : colors.border,
                    backgroundColor: selected ? colors.surfaceAlt : 'transparent',
                  },
                ]}>
                <Icon size={18} color={colors.text} strokeWidth={1.75} />
                <View style={styles.audienceText}>
                  <Text style={[styles.audienceLabel, { color: colors.text }]}>
                    {USER_ROLE_META[id].label}
                  </Text>
                  <Text style={[styles.audienceHint, { color: colors.textSecondary }]}>
                    {AUDIENCES[id]?.headline}
                  </Text>
                </View>
              </Pressable>
            );
          })}
        </View>

        <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Email addresses</Text>
        <TextInput
          value={recipients}
          onChangeText={setRecipients}
          placeholder="One per line, or separated by commas"
          placeholderTextColor={colors.textTertiary}
          multiline
          numberOfLines={4}
          autoCapitalize="none"
          keyboardType="email-address"
          testID="audience-invite-emails"
          accessibilityLabel="Email addresses"
          style={[
            styles.input,
            styles.textArea,
            { color: colors.text, borderColor: colors.border },
          ]}
        />
        <Text
          style={[styles.helperText, { color: tooMany ? colors.danger : colors.textSecondary }]}
          testID="audience-invite-count">
          {emails.length === 0
            ? `Up to ${MAX_RECIPIENTS} at a time.`
            : tooMany
              ? `${emails.length} addresses. Send at most ${MAX_RECIPIENTS} at a time.`
              : `${emails.length} ${emails.length === 1 ? 'address' : 'addresses'}.`}
        </Text>

        <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
          Community (optional)
        </Text>
        <TextInput
          value={areaName}
          onChangeText={setAreaName}
          placeholder="Big Bear Valley"
          placeholderTextColor={colors.textTertiary}
          testID="audience-invite-area"
          accessibilityLabel="Community"
          style={[styles.input, { color: colors.text, borderColor: colors.border }]}
        />

        <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
          A line from you (optional)
        </Text>
        <TextInput
          value={note}
          onChangeText={(t) => setNote(t.slice(0, MAX_NOTE))}
          placeholder="Ray suggested I get in touch."
          placeholderTextColor={colors.textTertiary}
          multiline
          numberOfLines={2}
          testID="audience-invite-note"
          accessibilityLabel="A line from you"
          style={[
            styles.input,
            styles.noteArea,
            { color: colors.text, borderColor: colors.border },
          ]}
        />

        <View style={styles.sendBlock}>
          <Button
            title="Send invitations"
            onPress={() => void handleSend()}
            disabled={!canSend}
            loading={sending}
            fullWidth
            testID="audience-invite-send"
          />
          {notice ? (
            <Text
              style={[
                styles.notice,
                { color: notice.isError ? colors.danger : colors.textSecondary },
              ]}
              testID="audience-invite-notice">
              {notice.text}
            </Text>
          ) : null}
          <Text style={[styles.helperText, { color: colors.textSecondary }]}>
            Everyone gets a one-click unsubscribe link. Anyone who has already unsubscribed is
            skipped.
          </Text>
        </View>

        <View testID="audience-invite-sheet-end" style={styles.endSentinel} />
      </AdaptiveSheet.ScrollView>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  content: { padding: SPACING.lg, gap: SPACING.xs },
  title: TYPOGRAPHY.h3,
  subtitle: { ...TYPOGRAPHY.bodySmall, marginBottom: SPACING.sm },
  sectionLabel: { ...TYPOGRAPHY.label, marginTop: SPACING.sm },
  audienceList: { gap: SPACING.xs },
  audienceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.md,
  },
  audienceText: { flex: 1 },
  audienceLabel: TYPOGRAPHY.h4,
  audienceHint: TYPOGRAPHY.caption,
  input: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.md,
    ...TYPOGRAPHY.body,
  },
  textArea: { minHeight: 96, textAlignVertical: 'top' },
  noteArea: { minHeight: 56, textAlignVertical: 'top' },
  helperText: TYPOGRAPHY.caption,
  sendBlock: { marginTop: SPACING.lg, gap: SPACING.xs },
  notice: TYPOGRAPHY.bodySmall,
  endSentinel: { height: 1 },
});
